import React, { useState, useEffect } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsForm from '../core/HeadersAndFooters/FooterButtonsForm';
import { useTranslation } from '../../i18n/useTranslation';

// Import supabaseFetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';

const MyTags = ({ userId, onBack, currentContext }) => {
  const { t } = useTranslation('user');
  const [preferredTags, setPreferredTags] = useState([]);
  const [antiTags, setAntiTags] = useState([]);
  const [preferredInput, setPreferredInput] = useState('');
  const [antiTagInput, setAntiTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Load existing tags from database
  const loadTags = React.useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseFetch.query(
        'user_tags',
        { select: '*', eq: { user_id: userId } }
      );

      if (error) throw error;

      if (data && data.length > 0) {
        const userTagData = data[0];
        setPreferredTags(userTagData.preferred_tags || []);
        setAntiTags(userTagData.anti_tags || []);
      }
    } catch (err) {
      console.error('Error loading tags:', err);
      setError(t('error_loading_tags'));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadTags();
  }, [userId, loadTags]);

  const handleAddPreferredTag = () => {
    if (preferredInput.trim()) {
      const newTags = preferredInput
        .split(',')
        .map(tag => tag.trim().toUpperCase())
        .filter(tag => tag && !preferredTags.includes(tag));
      
      if (newTags.length > 0) {
        setPreferredTags([...preferredTags, ...newTags]);
        setPreferredInput('');
      }
    }
  };

  const handleAddAntiTag = () => {
    if (antiTagInput.trim()) {
      const newTags = antiTagInput
        .split(',')
        .map(tag => tag.trim().toUpperCase())
        .filter(tag => tag && !antiTags.includes(tag));
      
      if (newTags.length > 0) {
        setAntiTags([...antiTags, ...newTags]);
        setAntiTagInput('');
      }
    }
  };

  const handleRemovePreferredTag = (tagToRemove) => {
    setPreferredTags(preferredTags.filter(tag => tag !== tagToRemove));
  };

  const handleRemoveAntiTag = (tagToRemove) => {
    setAntiTags(antiTags.filter(tag => tag !== tagToRemove));
  };

  const handleCancel = () => {
    // Reload original tags and go back
    loadTags();
    onBack();
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const { data: existing } = await supabaseFetch.query(
        'user_tags',
        { select: 'id', eq: { user_id: userId }, single: true }
      );

      if (existing) {
        const { error } = await supabaseFetch.update(
          'user_tags',
          {
            preferred_tags: preferredTags,
            anti_tags: antiTags,
            updated_at: new Date().toISOString()
          },
          { eq: { user_id: userId } }
        );

        if (error) throw error;
      } else {
        const { error } = await supabaseFetch.insert(
          'user_tags',
          {
            user_id: userId,
            preferred_tags: preferredTags,
            anti_tags: antiTags
          }
        );

        if (error) throw error;
      }

      onBack();
    } catch (err) {
      console.error('Error saving tags:', err);
      setError(t('error_saving_tags'));
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading_tags')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Desktop Header Bar */}
      {/* Header Container (passes currentContext for display) */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={currentContext}
        onBack={onBack}
        headerTitle={t('my_tags_title')}
      />

      {/* Main Content */}
      <div style={{
        marginTop: '80px',
        marginBottom: '120px',
        paddingTop: '25px',
        paddingBottom: '25px'
      }}>
        <div className="max-w-[1600px]" style={{ marginLeft: '40px' }}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="flex gap-8">
            {/* Left Column - Reserved Space (Fixed 480px) */}
            <div className="space-y-3" style={{ width: '480px', flexShrink: 0 }}>
              {/* Reserved for future functionality */}
            </div>

            {/* Right Column - Tags Content (Flexible) */}
            <div className="flex-1">
              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Preferred Tags */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-2">{t('preferred_tags_title')}</h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('preferred_tags_description')}
              </p>

              {/* Input Area */}
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={preferredInput}
                  onChange={(e) => setPreferredInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPreferredTag()}
                  placeholder={t('preferred_tags_placeholder')}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <button
                  onClick={handleAddPreferredTag}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
                >
                  {t('button_add')}
                </button>
              </div>

              {/* Tag Pills Display Area */}
              <div className="bg-white rounded-lg p-6 min-h-[300px]">
                <div className="flex flex-wrap gap-3">
                  {preferredTags.map((tag, index) => (
                    <button
                      key={index}
                      onClick={() => handleRemovePreferredTag(tag)}
                      className="bg-teal-500 text-white px-5 py-2 rounded-full flex items-center gap-2 hover:bg-teal-600 transition-colors text-sm font-medium"
                    >
                      <span>{tag}</span>
                      <span className="text-lg leading-none">×</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Anti-Tags */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-2">{t('anti_tags_title')}</h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('anti_tags_description')}
              </p>

              {/* Input Area */}
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={antiTagInput}
                  onChange={(e) => setAntiTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAntiTag()}
                  placeholder={t('anti_tags_placeholder')}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                />
                <button
                  onClick={handleAddAntiTag}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors"
                >
                  {t('button_add')}
                </button>
              </div>

              {/* Anti-Tag Pills Display Area */}
              <div className="bg-white rounded-lg p-6 min-h-[300px]">
                <div className="flex flex-wrap gap-3">
                  {antiTags.map((tag, index) => (
                    <button
                      key={index}
                      onClick={() => handleRemoveAntiTag(tag)}
                      className="bg-red-500 text-white px-5 py-2 rounded-full flex items-center gap-2 hover:bg-red-600 transition-colors text-sm font-medium"
                    >
                      <span>{tag}</span>
                      <span className="text-lg leading-none">×</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FooterContainer viewType="form" currentView="My Tags">
        <FooterButtonsForm
          onCancel={handleCancel}
          onCreateAndExit={handleSave}
          isEditMode={true}
        />
      </FooterContainer>
    </div>
  );
};

export default MyTags;
